import { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import {
  TrendingUp, Wallet, Receipt, Stethoscope, FlaskConical, Plane,
  Landmark, PiggyBank, Users, Loader2, Info,
} from "lucide-react";
import { api } from "@/lib/api";
import { eur } from "@/lib/constants";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";

const GOLD = "#D4AF37";
const COST_COLORS = ["#0F172A", "#334155", "#64748B", "#94A3B8", "#D4AF37"];

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="stat-lift bg-white rounded-xl border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-gold" : "text-slate-400"}`} strokeWidth={2} />
      </div>
      <p className="font-heading text-2xl font-semibold text-navy mt-3 tabular-nums">{value}</p>
    </div>
  );
}

export default function OwnerDashboard() {
  const t = useT();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    setData(null);
    api.get(`/dashboard/owner?period=${period}`).then((r) => setData(r.data)).catch(() => {});
  }, [period]);

  const PERIODS = [
    { k: "month", l: t("dashboard.periodMonth") }, { k: "quarter", l: t("dashboard.periodQuarter") },
    { k: "ytd", l: t("dashboard.periodYtd") }, { k: "all", l: t("dashboard.periodAll") },
  ];

  if (!data)
    return (
      <div className="flex items-center justify-center h-full p-20">
        <Loader2 className="h-6 w-6 animate-spin text-navy" />
      </div>
    );

  const k = data.kpis;

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <PageHeader owner title={t("dashboard.ownerTitle")} subtitle={t("dashboard.ownerSubtitle")}>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button key={p.k} data-testid={`period-${p.k}`} onClick={() => setPeriod(p.k)}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${period === p.k ? "bg-navy text-white" : "text-slate-500 hover:text-navy"}`}>
                {p.l}
              </button>
            ))}
          </div>
          {data.synthetic ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 text-navy px-3 py-1 text-xs font-semibold">
              <Info className="h-3.5 w-3.5" /> {t("dashboard.synthetic")}
            </span>
          ) : (
            <span data-testid="owner-live-badge" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t("dashboard.live")}
            </span>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-revenue" icon={TrendingUp} accent label={t("dashboard.earnedRevenue")} value={eur(k.earned_revenue)} />
        <Kpi testid="kpi-collections" icon={Wallet} label={t("dashboard.cashCollections")} value={eur(k.cash_collections)} />
        <Kpi testid="kpi-receivables" icon={Receipt} label={t("dashboard.receivables")} value={eur(k.receivables)} />
        <Kpi testid="kpi-margin" icon={PiggyBank} accent label={t("dashboard.margin")} value={eur(k.contribution_margin)} />
        <Kpi testid="kpi-doctor" icon={Stethoscope} label={t("nav.payables")} value={eur(k.doctor_liabilities)} />
        <Kpi testid="kpi-lab" icon={FlaskConical} label={t("nav.laboratory")} value={eur(k.laboratory_costs)} />
        <Kpi testid="kpi-travel" icon={Plane} label={t("nav.travel")} value={eur(k.travel_costs)} />
        <Kpi testid="kpi-bank" icon={Landmark} label={t("dashboard.bankBalance")} value={eur(k.bank_balance)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-navy rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-lg font-medium">{t("dashboard.revenueVsCollections")}</h3>
            <span className="text-xs text-white/40">{t("dashboard.last6Months")}</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.revenue_trend} margin={{ left: -12, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff18" vertical={false} />
              <XAxis dataKey="month" stroke="#ffffff55" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#ffffff55" fontSize={12} tickLine={false} axisLine={false}
                tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip
                contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 8, color: "#fff" }}
                formatter={(v) => eur(v)}
              />
              <Line type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={3} dot={{ r: 3, fill: GOLD }} />
              <Line type="monotone" dataKey="collections" stroke="#60A5FA" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading text-lg font-medium text-navy mb-6">{t("dashboard.costBreakdown")}</h3>
          <div className="space-y-4">
            {(() => {
              const max = Math.max(...data.cost_breakdown.map((c) => c.value), 1);
              return data.cost_breakdown.map((c, i) => (
                <div key={c.name}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-slate-600">{t(`dashboard.cost${c.name.replace(/\s/g, "")}`, c.name)}</span>
                    <span className="text-sm font-semibold text-navy tabular-nums">{eur(c.value)}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(c.value / max) * 100}%`, backgroundColor: COST_COLORS[i % COST_COLORS.length] }}
                    />
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
        <Kpi testid="kpi-patients" icon={Users} label={t("dashboard.totalPatients")} value={data.counts.total_patients} />
        <Kpi testid="kpi-staff" icon={Users} label={t("dashboard.activeStaff")} value={data.counts.active_staff} />
        <Kpi icon={Wallet} label={t("dashboard.cashOnHand")} value={eur(k.cash_on_hand)} />
        <Kpi icon={Receipt} label={t("dashboard.operatingExpenses")} value={eur(k.operating_expenses)} />
      </div>
    </div>
  );
}
